/**
 * characterTheme.js — derive a unique chat theme from a character's tags/category.
 *
 * Used in Chat.js to colour the chat background gradient + accent so every
 * character feels visually distinct. Deterministic per character so the
 * theme is stable across sessions.
 */

const THEMES = {
  vampire:    { from: '#1A0510', via: '#2A0A1A', to: '#0A0508', accent: '#E11D48', shadow: 'rgba(225,29,72,0.35)' },
  goth:       { from: '#0A0A14', via: '#1A0F20', to: '#06060C', accent: '#A78BFA', shadow: 'rgba(167,139,250,0.30)' },
  yandere:    { from: '#180A0F', via: '#2A0610', to: '#0A0408', accent: '#FB7185', shadow: 'rgba(251,113,133,0.35)' },
  femcel:     { from: '#0E0B14', via: '#1A1320', to: '#0A0710', accent: '#C4B5FD', shadow: 'rgba(196,181,253,0.30)' },
  shy:        { from: '#0E1218', via: '#161D26', to: '#0A0D12', accent: '#67E8F9', shadow: 'rgba(103,232,249,0.25)' },
  bestfriend: { from: '#101418', via: '#1A2128', to: '#0A0D10', accent: '#34D399', shadow: 'rgba(52,211,153,0.28)' },
  fantasy:    { from: '#0A0C18', via: '#141A2E', to: '#06080F', accent: '#A78BFA', shadow: 'rgba(167,139,250,0.30)' },
  scifi:      { from: '#040810', via: '#06121E', to: '#02050A', accent: '#22D3EE', shadow: 'rgba(34,211,238,0.30)' },
  anime:      { from: '#16091C', via: '#250E2A', to: '#0A050E', accent: '#F472B6', shadow: 'rgba(244,114,182,0.32)' },
  romance:    { from: '#1A0810', via: '#2A0A14', to: '#0A0408', accent: '#F472B6', shadow: 'rgba(244,114,182,0.35)' },
  villain:    { from: '#100308', via: '#1F050C', to: '#0A0205', accent: '#EF4444', shadow: 'rgba(239,68,68,0.35)' },
  knight:     { from: '#0A0E1A', via: '#141828', to: '#06080E', accent: '#FCD34D', shadow: 'rgba(252,211,77,0.30)' },
  isekai:     { from: '#0A0C1C', via: '#1A1A35', to: '#06070F', accent: '#818CF8', shadow: 'rgba(129,140,248,0.30)' },
  monster:    { from: '#080F0A', via: '#0E1F12', to: '#040806', accent: '#84CC16', shadow: 'rgba(132,204,22,0.28)' },
  cute:       { from: '#180814', via: '#251029', to: '#0A040A', accent: '#FB7185', shadow: 'rgba(251,113,133,0.30)' },
  rich:       { from: '#0A0A05', via: '#1A1407', to: '#050503', accent: '#FCD34D', shadow: 'rgba(252,211,77,0.32)' },
  brat:       { from: '#160510', via: '#28081C', to: '#0A0308', accent: '#EC4899', shadow: 'rgba(236,72,153,0.35)' },
  dom:        { from: '#0A0408', via: '#1A0610', to: '#060205', accent: '#E11D48', shadow: 'rgba(225,29,72,0.35)' },
  sub:        { from: '#100614', via: '#1E0A26', to: '#0A030E', accent: '#C084FC', shadow: 'rgba(192,132,252,0.30)' },
  default:    { from: '#0A0A0A', via: '#111318', to: '#050505', accent: '#E11D48', shadow: 'rgba(225,29,72,0.25)' },
};

const TAG_KEYWORDS = {
  vampire: ['vampire', 'blood', 'fang'],
  goth: ['goth', 'gothic', 'witch', 'occult', 'dark'],
  yandere: ['yandere', 'obsessive', 'crazy'],
  femcel: ['femcel', 'depressed', 'depressive', 'lonely'],
  shy: ['shy', 'introvert', 'kuudere', 'quiet'],
  bestfriend: ['bestfriend', 'best friend', 'friend', 'roommate', 'wholesome'],
  fantasy: ['fantasy', 'elf', 'magic', 'wizard', 'mage', 'fairy'],
  scifi: ['scifi', 'sci-fi', 'cyberpunk', 'space', 'robot', 'android'],
  anime: ['anime', 'manga', 'tsundere', 'maid', 'cat girl', 'neko'],
  romance: ['romance', 'romantic', 'love', 'crush', 'lover', 'boyfriend', 'girlfriend'],
  villain: ['villain', 'evil', 'enemy', 'rival', 'demon', 'devil'],
  knight: ['knight', 'warrior', 'samurai', 'soldier', 'hero'],
  isekai: ['isekai', 'world', 'reborn', 'rpg'],
  monster: ['monster', 'beast', 'werewolf', 'orc'],
  cute: ['cute', 'kawaii', 'sweet', 'innocent', 'pure'],
  rich: ['rich', 'wealthy', 'ceo', 'noble', 'princess', 'prince', 'royal'],
  brat: ['brat', 'bratty', 'tsundere', 'rude', 'spoiled'],
  dom: ['dom', 'dominant', 'alpha', 'strict'],
  sub: ['sub', 'submissive', 'shy', 'soft'],
};

function pickThemeKey(character) {
  if (!character) return 'default';
  const haystack = [
    character.category || '',
    character.name || '',
    (character.tags || []).join(' '),
    (character.description || '').slice(0, 100),
  ].join(' ').toLowerCase();
  // Find first matching keyword
  for (const [key, keywords] of Object.entries(TAG_KEYWORDS)) {
    for (const kw of keywords) {
      if (haystack.includes(kw)) return key;
    }
  }
  return 'default';
}

export function getCharacterTheme(character) {
  return THEMES[pickThemeKey(character)] || THEMES.default;
}
